/**
 * Project list, search, portability and the trash (CLAUDE.md 10.2, 11.3, 13).
 *
 * Soft delete exists because engineers delete projects by accident; a trash the user
 * can reach is the half of that feature that was missing -- `deleted_at` and a
 * restore endpoint are of no use if nothing renders them.
 *
 * **Search is server-side.** It used to filter the array this page had already
 * loaded, which meant a *position* could never be found: the client never had them.
 * "B1" or "P03" is exactly what an engineer types when returning to a job, so the two
 * kinds of hit are listed separately rather than blended.
 *
 * **Export and import** make a project portable (section 13). The bundle is parsed in
 * the browser, refused there if it is oversized or not a bundle at all, and every
 * refusal the server makes afterwards is shown in its own words -- those messages are
 * already translated, and "import failed" tells nobody anything.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  Download,
  FileStack,
  FileText,
  FolderOpen,
  MoreVertical,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import {
  useCreateProject,
  useDeleteProject,
  useImportProject,
  useProjectSearch,
  useProjects,
  useRestoreProject,
} from '@/api/hooks'
import type { Project } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { ProjectReportButton } from '@/components/calc/ProjectReportButton'
import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput, Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { downloadFile } from '@/lib/download'
import { formatRelative } from '@/lib/format'
import { MAX_BUNDLE_MB, readProjectBundle } from '@/lib/projectBundle'

const SEARCH_DEBOUNCE_MS = 300

export function ProjectsPage() {
  const { t, i18n } = useTranslation()
  const { workspaceId } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [showTrash, setShowTrash] = useState(false)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [creating, setCreating] = useState(false)
  const [reportProjectId, setReportProjectId] = useState<number | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)

  const { data: projects, isLoading } = useProjects(workspaceId, showTrash)
  const createProject = useCreateProject(workspaceId)
  const deleteProject = useDeleteProject(workspaceId)
  const restoreProject = useRestoreProject(workspaceId)
  const importProject = useImportProject(workspaceId)

  // A keystroke is not a query. 300 ms is long enough that typing "B1-beam" is one
  // request rather than seven, and short enough to feel immediate.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [search])

  // The search endpoint does not return deleted projects, so the trash keeps the
  // local filter -- it is the only way to find something already in the bin.
  const serverSearch = !showTrash && debounced.trim().length > 0
  const { data: results, isFetching: searching } = useProjectSearch(
    workspaceId,
    serverSearch ? debounced : '',
  )

  const visible = useMemo(() => {
    const rows = (projects ?? []).filter((p) => (showTrash ? p.deleted_at : !p.deleted_at))
    if (!showTrash || !search.trim()) return rows
    const needle = search.toLowerCase()
    return rows.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        (p.number ?? '').toLowerCase().includes(needle) ||
        (p.client ?? '').toLowerCase().includes(needle),
    )
  }, [projects, showTrash, search])

  const onExport = async (project: Project) => {
    const saved = await downloadFile(`/api/v1/projects/${project.id}/export`)
    if (!saved) toast({ title: t('common.error'), variant: 'error' })
  }

  const onImportFile = async (file: File) => {
    const read = await readProjectBundle(file)
    if (!read.ok) {
      // The server's own words for the same refusal, so the two can never disagree.
      toast({
        title: t('project.import'),
        description: t(read.messageKey, { ...read.params, ns: ['errors', 'common'] }),
        variant: 'error',
      })
      return
    }
    try {
      const project = await importProject.mutateAsync(read.bundle)
      toast({ title: t('project.imported'), variant: 'success' })
      navigate(`/projects/${project.id}`)
    } catch (error) {
      toast({
        title: t('project.import'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  return (
    <WorkspaceChrome title={t('nav.projects')}>
      <div className="mx-auto max-w-5xl space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              size={16}
              aria-hidden
              className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <Input
              type="search"
              aria-label={t('action.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="ps-9"
            />
          </div>

          <Button
            variant={showTrash ? 'primary' : 'secondary'}
            onClick={() => setShowTrash((value) => !value)}
          >
            <Trash2 size={16} aria-hidden />
            {t('nav.trash')}
          </Button>

          {/* The input is hidden rather than absent: a bare `<input type="file">`
              cannot be styled to match, and the button carries the same label. */}
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              // Cleared before the await, so choosing the same file twice fires again.
              event.target.value = ''
              if (file) void onImportFile(file)
            }}
          />
          <Button onClick={() => fileInput.current?.click()} loading={importProject.isPending}>
            <Upload size={16} aria-hidden />
            {t('project.import')}
          </Button>

          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus size={16} aria-hidden />
            {t('project.new')}
          </Button>
        </div>

        <p className="text-xs text-text-muted">
          {t('project.import_hint', { max: MAX_BUNDLE_MB })}
        </p>

        {serverSearch && (
          <div className="space-y-4" aria-busy={searching}>
            {/* Two kinds of hit, kept apart. A position and a project are not
                interchangeable, and blending them makes the list unreadable. */}
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">{t('nav.projects')}</h2>
              {(results?.projects.length ?? 0) === 0 ? (
                <p className="text-sm text-text-muted">{t('search.no_projects')}</p>
              ) : (
                <ul className="space-y-2">
                  {results?.projects.map((project) => (
                    <li key={project.id}>
                      <Card className="p-3">
                        <Link
                          to={`/projects/${project.id}`}
                          className="block font-medium hover:text-accent hover:underline"
                        >
                          {project.name}
                        </Link>
                        <p className="truncate text-xs text-text-muted">
                          {[project.number, project.client].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </Card>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <FileStack size={15} className="text-text-muted" aria-hidden />
                {t('search.positions')}
              </h2>
              {(results?.positions.length ?? 0) === 0 ? (
                <p className="text-sm text-text-muted">{t('search.no_positions')}</p>
              ) : (
                <ul className="space-y-2">
                  {results?.positions.map((position) => (
                    <li key={position.id}>
                      <Card className="p-3">
                        <Link
                          to={`/positions/${position.id}`}
                          className="flex flex-wrap items-center gap-2"
                        >
                          <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs font-medium">
                            {position.short_code}
                          </span>
                          <span className="tabular shrink-0 text-xs text-text-muted">
                            {`P${String(position.seq).padStart(2, '0')}`}
                          </span>
                          <span className="min-w-0 flex-1 truncate font-medium hover:text-accent">
                            {position.name}
                          </span>
                          <span className="min-w-0 truncate text-xs text-text-muted">
                            {position.project_name}
                          </span>
                        </Link>
                      </Card>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}

        {!serverSearch && isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {!serverSearch && !isLoading && visible.length === 0 && (
          <Card>
            <CardBody className="py-10 text-center">
              <FolderOpen size={28} className="mx-auto mb-2 text-text-muted" aria-hidden />
              <p className="font-medium">{t('empty.no_projects')}</p>
              <p className="mt-1 text-sm text-text-muted">{t('empty.no_projects_hint')}</p>
            </CardBody>
          </Card>
        )}

        {!serverSearch && (
          <ul className="space-y-2">
            {visible.map((project) => (
              <li key={project.id}>
                <Card className="flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    {project.deleted_at ? (
                      <span className="font-medium">{project.name}</span>
                    ) : (
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-medium hover:text-accent hover:underline"
                      >
                        {project.name}
                      </Link>
                    )}
                    <p className="truncate text-xs text-text-muted">
                      {[project.number, project.client].filter(Boolean).join(' · ') || '—'}
                      {' · '}
                      {t('common.updated')} {formatRelative(project.updated_at, i18n.language)}
                    </p>
                  </div>

                  {project.deleted_at ? (
                    <Button
                      size="sm"
                      onClick={async () => {
                        await restoreProject.mutateAsync(project.id)
                        toast({ title: t('action.restore'), variant: 'success' })
                      }}
                    >
                      <RotateCcw size={15} aria-hidden />
                      {t('action.restore')}
                    </Button>
                  ) : (
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger
                        aria-label={t('common.actions')}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-muted hover:bg-surface-raised hover:text-text"
                      >
                        <MoreVertical size={16} aria-hidden />
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="end"
                          sideOffset={6}
                          className="z-50 min-w-[13rem] rounded-md border border-border bg-surface-raised p-1 shadow-[var(--shadow-md)]"
                        >
                          <DropdownMenu.Item
                            onSelect={() => void onExport(project)}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none data-[highlighted]:bg-surface data-[highlighted]:text-text"
                          >
                            <Download size={14} aria-hidden />
                            <span className="flex-1">{t('project.export')}</span>
                          </DropdownMenu.Item>

                          <DropdownMenu.Item
                            onSelect={() => setReportProjectId(project.id)}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none data-[highlighted]:bg-surface data-[highlighted]:text-text"
                          >
                            <FileText size={14} aria-hidden />
                            <span className="flex-1">{t('report.project')}</span>
                          </DropdownMenu.Item>

                          <DropdownMenu.Separator className="my-1 h-px bg-border" />

                          <DropdownMenu.Item
                            onSelect={async () => {
                              try {
                                await deleteProject.mutateAsync(project.id)
                                toast({
                                  title: t('action.delete'),
                                  description: t('nav.trash'),
                                  variant: 'info',
                                })
                              } catch (error) {
                                toast({
                                  title: t('common.error'),
                                  description:
                                    error instanceof ApiError ? error.localised : undefined,
                                  variant: 'error',
                                })
                              }
                            }}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-[var(--status-fail)] outline-none data-[highlighted]:bg-surface"
                          >
                            <Trash2 size={14} aria-hidden />
                            <span className="flex-1">{t('action.delete')}</span>
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      {reportProjectId !== null && (
        <ProjectReportButton
          projectId={reportProjectId}
          open
          onOpenChange={(open) => !open && setReportProjectId(null)}
        />
      )}

      <NewProjectDialog
        open={creating}
        onOpenChange={setCreating}
        onSubmit={async (input) => {
          try {
            await createProject.mutateAsync(input)
            setCreating(false)
          } catch (error) {
            toast({
              title: t('common.error'),
              description: error instanceof ApiError ? error.localised : undefined,
              variant: 'error',
            })
          }
        }}
      />
    </WorkspaceChrome>
  )
}

function NewProjectDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: { name: string; number?: string; client?: string }) => Promise<void>
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({ name: '', number: '', client: '' })
  const [pending, setPending] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!form.name.trim()) return
    setPending(true)
    try {
      await onSubmit({
        name: form.name.trim(),
        number: form.number.trim() || undefined,
        client: form.client.trim() || undefined,
      })
      setForm({ name: '', number: '', client: '' })
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('project.new')}</DialogTitle>
          <DialogDescription>{t('empty.no_projects_hint')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <Field>
            <FieldLabel>{t('common.name')}</FieldLabel>
            <FieldInput
              required
              // Inside a modal that the user just opened deliberately. Moving focus to
              // the first field is the expected behaviour here, not a focus steal --
              // the rule's concern is autoFocus on page load.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel hint={t('common.optional')}>{t('project.number')}</FieldLabel>
            <FieldInput
              value={form.number}
              onChange={(event) => setForm({ ...form, number: event.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel hint={t('common.optional')}>{t('project.client')}</FieldLabel>
            <FieldInput
              value={form.client}
              onChange={(event) => setForm({ ...form, client: event.target.value })}
            />
          </Field>

          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={pending}>
              {t('action.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
